import { atelierHubUrl } from '@lib/env';
import { initials } from '@lib/initials';
import { ThemeSwitch } from '@shared/theme';
import { IconExternal } from '@shared/ui';

import type { Operator } from '@lib/operator';

/**
 * Topbar. The identity on the right is the **real** Google account from Atelier's
 * shared session — visible proof to the employee that they are signed in as
 * themselves without ever having logged into this app.
 *
 * There is no sign-out here on purpose: Atelier owns the session, and clearing
 * `vv_session` from the Backoffice would sign the user out of every internal
 * tool while leaving the Google/Cognito session alive to silently sign them back
 * in. The app-switcher link sends them to the Hub instead.
 *
 * Everything here is functional. The mockup's search, tenant switcher,
 * notifications, help, and environment pill were removed rather than shipped
 * inert — each returns when it has something real behind it. In particular
 * there is no environment indicator because there is no environment dimension:
 * the AWS account the app runs in *is* the environment (ADR 0002), and today
 * only production exists.
 */
export function Topbar({ operator }: { operator: Operator }) {
    return (
        <div className="flex h-full items-center gap-4 px-5">
            <a
                href={atelierHubUrl()}
                className="flex items-center gap-2 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-[13px] font-medium text-content-body hover:bg-surface-sunken"
            >
                <span className="size-4 rounded bg-content-strong" />
                Atelier Hub
                <IconExternal className="size-3.5 text-content-faint" />
            </a>

            <div className="flex-1" />

            {/*
             * The theme switch lives here rather than in the sidebar because
             * the sidebar is navigation — every other thing in it goes
             * somewhere — and this goes nowhere. The topbar is already where
             * the app keeps the controls that act on the shell itself rather
             * than on the data below it.
             */}
            <ThemeSwitch />

            <div className="flex items-center gap-2.5 border-l border-line pl-3">
                <div className="hidden text-right leading-tight lg:block">
                    <div className="text-[13px] font-semibold text-content">{operator.name}</div>
                    <div className="text-[11px] text-content-muted">{operator.email}</div>
                </div>
                {/*
                 * Initials, not the Google `avatarUrl` the session carries: the
                 * mockup specifies initials, and rendering a remote avatar would
                 * make an internal admin tool fetch an image from a third-party
                 * host on every page. The URL stays on the operator type if a
                 * later design wants it.
                 */}
                <div
                    aria-hidden="true"
                    className="flex size-8 items-center justify-center rounded-full border border-accent-line bg-accent-surface text-xs font-semibold text-accent-text"
                >
                    {initials(operator.name)}
                </div>
            </div>
        </div>
    );
}
